import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Menu, MessageCircle } from 'lucide-react'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import logoUrl from '../assets/logo-super-fut-kids-0b6c9.png'

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  const whatsappUrl =
    'https://wa.me/5511963087549?text=Quero%20saber%20mais%20sobre%20o%20SuperFut%20Kids'

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navItems = [
    { label: 'Benefícios', href: '#benefits' },
    { label: 'Modalidades', href: '#modalities' },
    { label: 'Área de Atendimento', href: '#map' },
    { label: 'Como Funciona', href: '#how-it-works' },
    { label: 'Blog', href: '/blog', isRoute: true },
  ]

  const handleNavClick = (href: string, isRoute: boolean = false) => {
    setIsMobileMenuOpen(false)

    if (isRoute) {
      navigate(href)
      window.scrollTo(0, 0)
      return
    }

    if (location.pathname !== '/') {
      navigate('/')
      // Small delay to allow navigation to complete before scrolling
      setTimeout(() => {
        const element = document.querySelector(href)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      }, 100)
    } else {
      const element = document.querySelector(href)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm py-3'
          : 'bg-transparent py-5',
      )}
    >
      <div className="container px-4 md:px-6 mx-auto flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center group">
          <img
            src={logoUrl}
            alt="SuperFut Kids"
            className="h-12 md:h-16 w-auto object-contain group-hover:scale-105 transition-transform"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            <button
              key={item.label}
              onClick={() => handleNavClick(item.href, item.isRoute)}
              className="text-sm font-medium text-slate-600 hover:text-super-green transition-colors bg-transparent border-none cursor-pointer"
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden md:block">
          <Button
            className="bg-super-blue hover:bg-sky-600 text-white font-bold rounded-full px-6"
            asChild
          >
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
              <MessageCircle className="w-4 h-4 mr-2" />
              Saber mais
            </a>
          </Button>
        </div>

        {/* Mobile Menu */}
        <div className="md:hidden">
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10">
                <Menu className="h-6 w-6 text-super-text" />
              </Button>
            </SheetTrigger>
            <SheetContent>
              <div className="flex flex-col gap-6 mt-10">
                {navItems.map((item) => (
                  <button
                    key={item.label}
                    onClick={() => handleNavClick(item.href, item.isRoute)}
                    className="text-left text-lg font-medium text-slate-700 hover:text-super-green transition-colors"
                  >
                    {item.label}
                  </button>
                ))}
                <Button
                  className="bg-super-yellow text-super-text hover:bg-yellow-400 font-bold rounded-full w-full mt-4"
                  asChild
                >
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Quero saber mais
                  </a>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  )
}
